import { addDays, addMonths, endOfDay, startOfDay } from 'date-fns';

export const DEFAULT_LOCALE = 'en-AU';

// Supported languages based on odin-translations (filtered to available primelocale files)
const supportedLocales = [
  'ja', // Japanese
  'zh-CN', // Chinese (Simplified) - Note: primelocale uses zh-CN instead of zh
  'zh-TW', // Chinese (Traditional)
  'fi', // Finnish
  'tl', // Filipino
  'hr', // Croatian
  'es', // Spanish
  'fr', // French
  'de', // German
  'it', // Italian
  'pt-BR', // Portuguese (Brazil)
  'pt', // Portuguese (Portugal)
  'hi', // Hindi
  'ar', // Arabic
  'ru', // Russian
  'ko', // Korean
  'bn', // Bengali
  'en', // English
  'en-GB', // English (United Kingdom)
  DEFAULT_LOCALE, // Always include default locale
];

// Vite-compatible loaders
const localeLoaders: Record<string, () => Promise<Record<string, unknown>>> = {
  ja: () => import('primelocale/js/ja.js'),
  'zh-CN': () => import('primelocale/js/zh_CN.js'),
  'zh-TW': () => import('primelocale/js/zh_TW.js'),
  fi: () => import('primelocale/js/fi.js'),
  tl: () => import('primelocale/js/tl.js'),
  hr: () => import('primelocale/js/hr.js'),
  es: () => import('primelocale/js/es.js'),
  fr: () => import('primelocale/js/fr.js'),
  de: () => import('primelocale/js/de.js'),
  it: () => import('primelocale/js/it.js'),
  'pt-BR': () => import('primelocale/js/pt_BR.js'),
  pt: () => import('primelocale/js/pt.js'),
  hi: () => import('primelocale/js/hi.js'),
  ar: () => import('primelocale/js/ar.js'),
  ru: () => import('primelocale/js/ru.js'),
  ko: () => import('primelocale/js/ko.js'),
  bn: () => import('primelocale/js/bn.js'),
  en: () => import('primelocale/js/en.js'),
  'en-GB': () => import('primelocale/js/en_GB.js'),
  [DEFAULT_LOCALE]: () => import('primelocale/js/en_AU.js'),
};

export const resolveLocale = async () => {
  try {
    const browserLocale = navigator?.language || DEFAULT_LOCALE;

    // Check if the browser locale is in our supported list
    let locale = browserLocale;
    if (!supportedLocales.includes(browserLocale)) {
      // Try to match language part only (e.g., 'en-US' -> 'en')
      const languageCode = browserLocale.split('-')[0];
      if (supportedLocales.includes(languageCode)) {
        locale = languageCode;
      } else {
        // Special case for Chinese: map 'zh' to 'zh-CN'
        if (languageCode === 'zh') {
          locale = 'zh-CN';
        } else {
          locale = DEFAULT_LOCALE;
        }
      }
    }

    // Use explicit loader for better Vite compatibility
    const loader = localeLoaders[locale] || localeLoaders[DEFAULT_LOCALE];
    const localeModule = await loader();
    const localeData = localeModule[locale.replace('-', '_')];
    return { locale, options: localeData };
  } catch {
    // Fallback to default locale if import fails
    const localeModule = await localeLoaders[DEFAULT_LOCALE]();
    const localeData = localeModule[DEFAULT_LOCALE.replace('-', '_')];
    return { locale: DEFAULT_LOCALE, options: localeData };
  }
};

/**
 * Parses a date string with a 2 or 4-digit year into a Date object.
 * Uses Intl.DateTimeFormat to determine the token order and separator for the given locale,
 * then rearranges the input into US format for the Date constructor.
 * @throws on invalid input.
 */
export const parseDateTime = (text: string, locale: string): Date => {
  const parts = new Intl.DateTimeFormat(locale).formatToParts(new Date(2000, 0, 2));
  const order = parts.filter((p) => ['day', 'month', 'year'].includes(p.type)).map((p) => p.type);
  const sep = parts.find((p) => p.type === 'literal')?.value.trim() || '/';

  const values = text.split(sep);
  if (values.length !== 3) {
    throw new Error('Invalid date');
  }

  const map: Record<string, string> = {};
  order.forEach((type, i) => {
    map[type] = values[i];
  });

  const date = new Date(`${map.month}/${map.day}/${map.year}`);
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid date');
  }

  return date;
};

export type ValidationType =
  | 'DATE_BEFORE_OR_ON_TODAY'
  | 'DATE_ON_OR_AFTER_TODAY'
  | 'DATE_TODAY_PLUS_ONE_MONTH'
  | 'DATE_TODAY_PLUS_CUSTOM_NB_OF_DAYS';

export interface FieldValidation {
  validationType: ValidationType;
  numberOfDays: number;
}

export interface DateBounds {
  minDate: Date | undefined;
  maxDate: Date | undefined;
}

/** Converts a field validation rule into min/max date bounds for the calendar. */
export const resolveDateBounds = (fieldValidations: FieldValidation | undefined): DateBounds => {
  if (!fieldValidations) {
    return { minDate: undefined, maxDate: undefined };
  }

  switch (fieldValidations.validationType) {
    case 'DATE_BEFORE_OR_ON_TODAY':
      return { minDate: undefined, maxDate: endOfDay(new Date()) };
    case 'DATE_ON_OR_AFTER_TODAY':
      return { minDate: startOfDay(new Date()), maxDate: undefined };
    case 'DATE_TODAY_PLUS_ONE_MONTH':
      return { minDate: startOfDay(new Date()), maxDate: addMonths(new Date(), 1) };
    case 'DATE_TODAY_PLUS_CUSTOM_NB_OF_DAYS':
      if (!Number.isFinite(fieldValidations.numberOfDays)) {
        // biome-ignore lint/suspicious/noConsole: intentional warning for runtime diagnostics
        console.warn(
          `resolveDateBounds: DATE_TODAY_PLUS_CUSTOM_NB_OF_DAYS has invalid numberOfDays "${String(fieldValidations.numberOfDays)}"`
        );
        return { minDate: startOfDay(new Date()), maxDate: undefined };
      }
      return {
        minDate: startOfDay(new Date()),
        maxDate: addDays(new Date(), fieldValidations.numberOfDays),
      };
    default: {
      // Compile-time exhaustiveness check — will error if a new ValidationType is added without a case above
      const _exhaustiveCheck: never = fieldValidations.validationType;
      // Gracefully handle unknown or undefined validationType at runtime (e.g. misconfigured field data)
      // biome-ignore lint/suspicious/noConsole: intentional warning for runtime diagnostics
      console.warn(`resolveDateBounds: unrecognised validationType "${String(_exhaustiveCheck)}"`);
      return { minDate: undefined, maxDate: undefined };
    }
  }
};
